import styled, { css } from 'styled-components';

import { CarValuationResponse } from '../../../../api/types/CarValuationApiTypes';
import { ButtonLink } from '../../../../components/Toolkit/Button/ButtonLink';
import { DetailList } from '../../../../components/Toolkit/DetailList/DetailList';

import {
  Title,
  SIoCheckmark,
} from '../../../../components/CarValuationPage/components/InfoPanelList/InfoPanelList';

import { Notification } from '../../../../components/Toolkit/Notification/Notification';
import { media } from '../../../../helpers/breakpoints';
import { formatPriceRange } from '../../../../helpers/formatting';
import { TCarValuationPageNames, PAGE } from '../../../../helpers/pages';
import { Link } from '../../../../components/Toolkit/Button/Link';
import { generateSimilarCarsLink } from '../../../../helpers/Links/Links';
import {
  FilterOptions,
  SortOptions,
} from '../../../../helpers/Links/Links.typed';
interface ICarSummaryPanel {
  text: string;
  registration: string;
  mileage: string;
  pageName: TCarValuationPageNames;
  vehicleMetaTitle: string;
  carValuationResponse: CarValuationResponse;
  cdnUrl: string;
  vehicleInfo: {
    make: string;
    model: string;
    year: string;
    fuelType: string;
    transmission: string;
    bodyType: string;
  };
}

export const SummaryWrapper = styled.div`
  margin: ${({ theme }) => `0 ${theme.spacing.S8}`};
  position: relative;
`;

export const SummaryPanel = styled.div<{ pageName: string }>`
  margin: ${({ pageName }) =>
    pageName === PAGE.INSTANT_OFFER_VALUATION
      ? `96px auto 0 auto;`
      : `137px auto 0 auto;`};
  width: 100%;

  ${media.large} {
    width: 760px;
    margin: ${({ pageName }) =>
      pageName === PAGE.INSTANT_OFFER_VALUATION
        ? '192px auto 0 auto'
        : '200px auto 0 auto'};
  }
`;

export const HeroTextContent = styled.div<{ pageName?: string }>`
  margin: ${({ theme, pageName }) =>
    pageName === PAGE.INSTANT_OFFER_VALUATION
      ? `0 auto ${theme.spacing.M24} auto;`
      : `0 auto ${theme.spacing.M24} auto;`};

  height: ${({ pageName }) =>
    pageName === PAGE.INSTANT_OFFER_VALUATION ? '96px' : '64px'};

  position: relative;

  ${media.large} {
    margin: ${({ theme }) => `192px auto ${theme.spacing.M32} auto`};
    height: 80px;
  }
`;

export const TextWrapper = styled.div`
  position: absolute;
  bottom: 0;
  right: 0;
  left: 0;

  h1 {
    ${({ theme }) => theme.fontSize.H24}
    color: ${({ theme }) => theme.colors.WHITE};
    margin: 0 auto;
    max-width: 290px;
    text-align: center;

    ${media.medium} {
      max-width: 592px;
    }

    ${media.large} {
      ${({ theme }) => theme.fontSize.H32}
      max-width: 592px;
    }
  }
`;

const SponsoredText = styled.div`
  text-align: center;
  margin: 0 auto;
  display: flex;
  justify-content: center;
  align-items: center;

  margin: ${({ theme }) => theme.spacing.M16} 0;

  ${media.large} {
    margin-bottom: 0;
  }

  p {
    ${({ theme }) => theme.fontSize.M14}
    color: white;
    font-weight: ${({ theme }) => theme.fontWeight.regular};
  }

  img {
    width: 102px;
    height: 24px;
    ${media.large} {
      width: 137px;
      height: 32px;
    }
  }
`;

const Wrapper = styled.div`
  background: ${({ theme }) => theme.colors.WHITE};
  border-radius: ${({ theme }) => theme.borderRadius.default};
  z-index: 1;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  text-align: center;
  padding: ${({ theme }) => `${theme.spacing.M24} ${theme.spacing.M16}`};

  ${media.large} {
    padding: ${({ theme }) => theme.spacing.M24};
  }
`;

const Container = styled.div`
  display: flex;
  flex: 1;
  justify-content: center;
  flex-direction: column;
  width: 100%;

  ${media.large} {
    max-width: 592px;
  }
`;

const VehicleInformation = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.M16};
`;

const Registration = styled.p`
  ${({ theme }) => theme.fontSize.M14};
`;

const CarName = styled.p`
  ${({ theme }) => theme.fontSize.M14};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
`;

export const SDetailList = styled(DetailList)`
  row-gap: ${({ theme }) => theme.spacing.M16};
  margin-bottom: 0;
`;

const SNotification = styled(Notification)`
  color: ${({ theme }) => theme.colors.GREY_DARKER};
  padding: ${({ theme }) => theme.spacing.S8};
  margin-bottom: ${({ theme }) => theme.spacing.M16};
  align-items: center;
  justify-content: center;
`;

const LeadText = styled.p`
  ${({ theme }) => theme.fontSize.M16};
  margin-bottom: ${({ theme }) => theme.spacing.S8};
`;

const PriceRangeStyles = css`
  ${({ theme }) => theme.fontSize.H24};
  color: ${({ theme }) => theme.colors.GREY_DARKER};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
`;

const PrivateValuationPriceRange = styled.p`
  ${PriceRangeStyles}
  margin-bottom: ${({ theme }) => theme.spacing.S8};
`;

const InstantOfferValuationPriceRange = styled.p`
  ${PriceRangeStyles}
`;

const DealerOfferTextGroup = styled.div`
  max-width: 420px;
  text-align: center;
  margin: ${({ theme }) => `0 auto ${theme.spacing.M16} auto`};
`;

const DealerOfferText = styled.p`
  ${({ theme }) => theme.fontSize.M14};
  display: inline;
`;

const DealerOfferTextValuation = styled.p`
  ${({ theme }) => theme.fontSize.M14};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  display: inline;
`;

const ButtonGroup = styled.div<{ gap?: boolean }>`
  display: grid;
  gap: ${({ theme }) => theme.spacing.S8};
  margin: ${({ theme, gap }) => (gap ? `0 0 ${theme.spacing.M16} 0` : 0)};

  ${media.large} {
    display: flex;
    gap: ${({ theme }) => theme.spacing.M16};
  }
`;

const SButtonLink = styled(ButtonLink)`
  ${media.large} {
    flex: 50%;
    margin: 0 auto;
    width: 240px;
  }
`;

const SimilarCarLinkText = styled.span`
  text-transform: none;
`;

const InstantOfferContent = styled.div``;
const CarValuationContent = styled.div``;

function CarSummaryPanel({
  text,
  registration,
  mileage,
  pageName,
  vehicleMetaTitle,
  carValuationResponse: { expectedDealerOfferRange, privateMarketOfferRange },
  cdnUrl,
  vehicleInfo,
}: ICarSummaryPanel) {
  const { make, model, year, fuelType, bodyType, transmission } = vehicleInfo;
  return (
    <SummaryWrapper>
      <SummaryPanel pageName={pageName}>
        <HeroTextContent pageName={pageName}>
          <TextWrapper>
            <h1>{text}</h1>
            {pageName === PAGE.PRIVATE_VALUATION && (
              <SponsoredText>
                <p>{'In partnership with '} </p>
                <img
                  src={`${cdnUrl}/images/thirdparty/Allianz.svg`}
                  alt="Allianz logo"
                />
              </SponsoredText>
            )}
          </TextWrapper>
        </HeroTextContent>
        <Wrapper>
          <Container>
            <VehicleInformation>
              <Registration>Your registration: {registration}</Registration>
              <CarName>{vehicleMetaTitle}</CarName>
            </VehicleInformation>

            {pageName === PAGE.INSTANT_OFFER_VALUATION ? (
              <>
                <InstantOfferContent>
                  <SNotification intent="INFO">
                    <>
                      <LeadText>Dealerships will offer between </LeadText>
                      <InstantOfferValuationPriceRange>
                        {formatPriceRange(expectedDealerOfferRange)}*
                      </InstantOfferValuationPriceRange>
                    </>
                  </SNotification>
                </InstantOfferContent>
                <ButtonGroup gap={true}>
                  <SButtonLink
                    href={`/publish-c2b/${registration}/${mileage}`}
                    target="_blank"
                  >
                    Request a dealer appointment
                  </SButtonLink>
                </ButtonGroup>
                <>
                  <Title>Why sell your car this way?</Title>
                  <SDetailList
                    list={[
                      "It's a hassle-free, professional service",
                      "It's the quickest way to sell your car",
                      'Get the best possible dealership offer',
                    ]?.map((item) => ({
                      title: item,
                      icon: <SIoCheckmark width="16" height="16" />,
                    }))}
                  />
                </>
              </>
            ) : (
              <CarValuationContent>
                <SNotification intent="INFO">
                  <>
                    <LeadText>Sell your car on DoneDeal for </LeadText>
                    <PrivateValuationPriceRange>
                      {formatPriceRange(privateMarketOfferRange)}*
                    </PrivateValuationPriceRange>
                    <Link
                      href={generateSimilarCarsLink(
                        make,
                        model,
                        year,
                        SortOptions.LEAST_EXPENSIVE,
                        {
                          sellerType: FilterOptions.PRIVATE_CARS,
                          fuelType,
                          transmission,
                          bodyType,
                        },
                      )}
                      ofType="SECONDARY"
                      target="_blank"
                    >
                      <SimilarCarLinkText>View similar cars</SimilarCarLinkText>
                    </Link>
                  </>
                </SNotification>

                <DealerOfferTextGroup>
                  <DealerOfferText>
                    Or sell it, hassle-free, to a trusted dealership for
                  </DealerOfferText>
                  <DealerOfferTextValuation>
                    {formatPriceRange(expectedDealerOfferRange)}
                  </DealerOfferTextValuation>
                </DealerOfferTextGroup>
                <ButtonGroup>
                  <SButtonLink href="/publish" target="_blank">
                    Sell my car on DoneDeal
                  </SButtonLink>
                  <SButtonLink
                    href={`/publish-c2b/${registration}/${mileage}`}
                    ofType="SECONDARY"
                  >
                    Sell my car to a trusted dealership
                  </SButtonLink>
                </ButtonGroup>
              </CarValuationContent>
            )}
          </Container>
        </Wrapper>
      </SummaryPanel>
    </SummaryWrapper>
  );
}

export { CarSummaryPanel };
